import { observable, computed } from 'mobx';
import {
  ActorRefFrom,
  DoneActorEvent,
  ErrorActorEvent,
  SnapshotFrom,
  assign,
  createActor,
  fromPromise,
  setup,
} from 'xstate';

import api from 'lib/api';
import {
  SelectOption,
  ChangeInPainLevelResponse,
  ChartLoadParams,
  ChartsFetchParams,
} from 'types';

import { ChangeInPainLevel } from '../change-in-pain-level.model';
import { ChangeInPainLevelSerializer } from '../serializer';

interface MachineContext {
  data?: ChangeInPainLevel;
  error?: Error;
  sessionsCount: number;
  selectedTherapists: Array<SelectOption<string>>;
  selectedPartners: Array<SelectOption<string>>;
  selectedClinics: Array<SelectOption<string>>;
  partnerId: string | undefined;
  time: string;
}

interface SelectedTherapistsEvent {
  type: 'THERAPISTS_SELECTED';
  ids: Array<SelectOption<string>>;
  partnerId: string | undefined;
  time: string;
}

interface SelectedPartnersEvent {
  type: 'PARTNERS_SELECTED';
  selected: Array<SelectOption<string>>;
}

interface SelectedClinicsEvent {
  type: 'CLINICS_SELECTED';
  selected: Array<SelectOption<string>>;
}

interface ChangeSessionCountEvent {
  type: 'CHANGE_SESSION_COUNT';
  value: number;
}

type MachineEvent =
  | {
      type: 'RETRY_EVENT';
    }
  | ChangeSessionCountEvent
  | SelectedTherapistsEvent
  | SelectedClinicsEvent
  | SelectedPartnersEvent
  | DoneActorEvent<ChangeInPainLevel>
  | ErrorActorEvent<Error>;

interface LoadParams extends ChartLoadParams {
  sessionsCount: number;
}
export class ChangeInPainLevelStore {
  @observable private current!: SnapshotFrom<typeof this.machine>;

  protected service!: ActorRefFrom<typeof this.machine>;

  constructor(private serializer: ChangeInPainLevelSerializer) {
    this.service = createActor(this.machine);
    this.service.subscribe((state) => {
      this.current = state;
    });
    this.service.start();
  }

  private machine = setup({
    types: {
      context: {} as MachineContext,
      events: {} as MachineEvent,
    },
    actions: {
      saveData: assign({
        data: ({ event }) => {
          const { output } = event as DoneActorEvent<ChangeInPainLevel>;

          return output;
        },
      }),
      saveError: assign({
        error: ({ event }) => {
          const { error } = event as ErrorActorEvent<Error>;

          return error;
        },
      }),
      deleteError: assign({
        error: undefined,
      }),
      changeSessionCount: assign({
        sessionsCount: ({ event }) => (event as ChangeSessionCountEvent).value,
      }),
      setTherapists: assign({
        selectedTherapists: ({ event }) =>
          (event as SelectedTherapistsEvent).ids,
        partnerId: ({ event }) => (event as SelectedTherapistsEvent).partnerId,
        time: ({ event }) => (event as SelectedTherapistsEvent).time,
      }),
      setPartners: assign({
        selectedPartners: ({ event }) =>
          (event as SelectedPartnersEvent).selected,
      }),
      setClinics: assign({
        selectedClinics: ({ event }) =>
          (event as SelectedClinicsEvent).selected,
      }),
    },
    actors: {
      fetchChangeInPainLevelModel: fromPromise<ChangeInPainLevel, LoadParams>(
        ({ input, signal }) =>
          this.doFetch(
            input.sessionsCount,
            input.therapists,
            input.partnerId,
            input.partners,
            input.clinics,
            input.time,
            signal,
          ),
      ),
    },
  }).createMachine({
    id: 'Change In Pain Level Machine',
    initial: 'initial',
    context: {
      data: undefined,
      error: undefined,
      sessionsCount: 5,
      selectedTherapists: [],
      selectedPartners: [],
      selectedClinics: [],
      partnerId: undefined,
      time: '90',
    },
    states: {
      initial: {
        on: {
          CHANGE_SESSION_COUNT: {
            target: 'fetching',
            actions: ['changeSessionCount'],
          },
          THERAPISTS_SELECTED: {
            target: 'fetching',
            actions: ['setTherapists'],
          },
          PARTNERS_SELECTED: { actions: ['setPartners'] },
          CLINICS_SELECTED: { actions: ['setClinics'] },
        },
      },
      fetching: {
        on: {
          CHANGE_SESSION_COUNT: {
            reenter: true,
            target: 'fetching',
            actions: ['changeSessionCount'],
          },
          THERAPISTS_SELECTED: {
            reenter: true,
            target: 'fetching',
            actions: ['setTherapists'],
          },
          PARTNERS_SELECTED: { actions: ['setPartners'] },
          CLINICS_SELECTED: { actions: ['setClinics'] },
        },
        invoke: {
          id: 'fetchChangeInPainLevelModel',
          src: 'fetchChangeInPainLevelModel',
          input: ({ event, context }) => ({
            sessionsCount: context.sessionsCount,
            therapists:
              (event as SelectedTherapistsEvent).ids ||
              context.selectedTherapists,
            partnerId:
              (event as SelectedTherapistsEvent).partnerId || context.partnerId,
            partners: context.selectedPartners,
            clinics: context.selectedClinics,
            time: (event as SelectedTherapistsEvent).time || context.time,
          }),
          onDone: {
            target: 'success',
            actions: ['saveData', 'deleteError'],
          },
          onError: {
            target: 'failure',
            actions: ['saveError'],
          },
        },
      },
      success: {
        on: {
          THERAPISTS_SELECTED: {
            target: 'fetching',
            actions: ['setTherapists'],
          },
          PARTNERS_SELECTED: { actions: ['setPartners'] },
          CLINICS_SELECTED: { actions: ['setClinics'] },
          CHANGE_SESSION_COUNT: {
            target: 'fetching',
            actions: ['changeSessionCount'],
          },
        },
      },
      failure: {
        on: {
          RETRY_EVENT: 'fetching',
          THERAPISTS_SELECTED: {
            target: 'fetching',
            actions: ['setTherapists'],
          },
          PARTNERS_SELECTED: { actions: ['setPartners'] },
          CLINICS_SELECTED: { actions: ['setClinics'] },
          CHANGE_SESSION_COUNT: {
            target: 'fetching',
            actions: ['changeSessionCount'],
          },
        },
      },
    },
  });

  private async load(
    payload: LoadParams,
    signal?: AbortSignal,
  ): Promise<ChangeInPainLevelResponse> {
    const { time, sessionsCount, therapists, partnerId, clinics, partners } =
      payload;
    const params = new URLSearchParams();
    params.set('from', time);
    params.set('sessions', sessionsCount.toString());
    let doctors: string[] = [];
    if (therapists.length === 0) {
      if (partnerId) {
        params.set('partner', partnerId);
      }
    } else {
      doctors = therapists.map(({ value }) => value);
    }
    const request = await api.post<ChangeInPainLevelResponse>(
      `/change_in_pain_level?${params.toString()}`,
      {
        data: {
          doctors,
          partners: partners.map((p) => p.value),
          clinics: clinics.map((c) => c.value),
        },
      },
      { signal },
    );
    return request.data;
  }

  public setPartners(selectedPartners: Array<SelectOption<string>>) {
    this.service.send({
      type: 'PARTNERS_SELECTED',
      selected: selectedPartners,
    });
  }

  public setClinics(selectedClinics: Array<SelectOption<string>>) {
    this.service.send({ type: 'CLINICS_SELECTED', selected: selectedClinics });
  }

  public fetch(params: ChartsFetchParams): void {
    const { therapists, partnerId, time } = params;
    this.service.send({
      type: 'THERAPISTS_SELECTED',
      ids: therapists,
      partnerId,
      time,
    });
  }

  private async doFetch(
    sessionsCount: number,
    therapists: Array<SelectOption<string>>,
    partnerId: string | undefined,
    partners: Array<SelectOption<string>>,
    clinics: Array<SelectOption<string>>,
    time: string,
    signal?: AbortSignal,
  ) {
    const jsonResponse = await this.load(
      {
        sessionsCount,
        therapists,
        partnerId,
        partners,
        clinics,
        time,
      },
      signal,
    );

    return this.serializer.deserialize(jsonResponse);
  }

  @computed
  public get cache(): ChangeInPainLevel | undefined {
    if (typeof this.current === 'undefined') {
      return undefined;
    }

    return this.current.context.data as ChangeInPainLevel;
  }

  @computed
  public get fetching(): boolean {
    if (typeof this.current === 'undefined') {
      return false;
    }

    return this.current.matches('fetching');
  }

  @computed
  public get fetched(): boolean {
    if (typeof this.current === 'undefined') {
      return false;
    }

    return this.current.matches('success');
  }

  @computed
  public get failure(): boolean {
    if (typeof this.current === 'undefined') {
      return false;
    }

    return this.current.matches('failure');
  }

  public refetch(): void {
    this.service.send({ type: 'RETRY_EVENT' });
  }

  @computed
  public get error(): Error | undefined {
    if (typeof this.current === 'undefined') {
      return undefined;
    }

    return this.current.context.error;
  }

  @computed public get sessionsCount(): number {
    if (typeof this.current === 'undefined') {
      return 5;
    }

    return this.current.context.sessionsCount;
  }

  public changeSessionCount(value: number): void {
    this.service.send({ type: 'CHANGE_SESSION_COUNT', value });
  }
}
